import { useMutation } from 'react-query';

import { axios } from '@/lib/axios';
import { MutationConfig, queryClient } from '@/lib/react-query';

import { useToast } from '@/components/ui/use-toast';
import { Organization } from '../types';
import { label } from '@/utils/label';
import { LabelGroup } from '@/features/label/types';

const QUERY_KEY = 'organizations';

export const deleteOrganization = ({ organizerId }: { organizerId: number }) => {
  return axios.delete(`/organizer/${organizerId}`);
};

type UseDeleteOrganizationOptions = {
  config?: MutationConfig<typeof deleteOrganization>;
};

export const useDeleteOrganization = ({ config }: UseDeleteOrganizationOptions) => {
  const { toast } = useToast();

  return useMutation({
    onMutate: async (deleted) => {
      await queryClient.cancelQueries([QUERY_KEY, deleted.organizerId]);

      const prev = queryClient.getQueryData<Organization[]>([QUERY_KEY]);

      queryClient.setQueryData(
        [QUERY_KEY],
        prev?.filter((user) => user?.organizerId !== deleted.organizerId)
      );

      return { prev };
    },
    onError: (_, __, context: any) => {
      if (context?.prev) {
        queryClient.setQueryData([QUERY_KEY], context.prev);
      }
      toast({
        variant: 'destructive',
        title: label('something_went_wrong', LabelGroup.GLOBAL),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEY]);
      queryClient.refetchQueries([QUERY_KEY]);

      toast({
        variant: 'success',
        title: label('organization_deleted', LabelGroup.ORGANIZER_ORGANIZATION_LIST),
      });
    },
    ...config,
    mutationFn: ({ organizerId }) => deleteOrganization({ organizerId }),
  });
};
